import React from "react";
import {
	Card,
	CardActionArea,
	CardMedia,
	Chip,
	Grid,
	Typography,
	useTheme,
	Tooltip,
	IconButton,
} from "@mui/material";
import { alpha, Box, darken, lighten } from "@mui/system";
import {
	DeleteOutlineOutlined,
	ExpandMore,
	LinkOutlined,
} from "@mui/icons-material";
import useStyles from "./styles";

export function getSafeTags(acesso) {
	if (!acesso) return [];

	if (acesso?.tags && Array.isArray(acesso?.tags)) return acesso?.tags;

	const rawRelations =
		acesso?.rlFormVMs ||
		acesso?.rlFormDBs ||
		acesso?.rlFormAcessWebTags ||
		acesso?.rlFormVpnTags ||
		[];

	return rawRelations.map((r) => r.tags || r.tag).filter((t) => t);
}

export default function CardSelection(params) {
	const {
		selected = false,
		onClick,
		status,
		title,
		ip,
		onDelete,
		acesso,
		windowWidth,
	} = params;

	const theme = useTheme();
	const styles = useStyles(theme);
	const TYPE_OPTIONS = [
		{
			label: "VM/Servidor",
			value: 0,
			color: theme?.palette?.chip?.main?.completed,
		},
		{
			label: "Banco de Dados",
			value: 1,
			color: theme?.palette?.chip?.main?.waitingReview,
		},
		{
			label: "Acesso Web",
			value: 2,
			color: theme?.palette?.chip?.main?.waitingReturn,
		},
		{
			label: "VPN",
			value: 3,
			color: theme?.palette?.chip?.main?.inProgress,
		},
	];

	const handleDeleteClick = (event) => {
		event.stopPropagation();
		if (onDelete) onDelete();
	};

	const handleOpenUrl = (urlToOpen) => {
		if (!urlToOpen) return;
		let finalUrl = urlToOpen;
		if (!urlToOpen.startsWith("http://") && !urlToOpen.startsWith("https://")) {
			finalUrl = `https://${urlToOpen}`;
		}
		window.open(finalUrl, "_blank");
	};

	const displayTags = getSafeTags(acesso);
	// ---------------------------------------------------------------------

	const currentStatus = status ?? acesso?.acessoTipo;
	const currentOption = TYPE_OPTIONS.find(
		(option) => option?.value === Number(currentStatus),
	);
	const borderColor = currentOption?.color || theme.palette.divider;
	const labelText = currentOption?.label;

	const isWebType = Number(currentStatus) === 2;
	const webAccesses = acesso?.web_accesses || [];

	//função pra pegar o icone

	const getFaviconUrl = (rawUrl) => {
		if (!rawUrl) return "";

		try {
			let validUrl = rawUrl;
			if (!rawUrl.startsWith("http://") && !rawUrl.startsWith("https://")) {
				validUrl = `https://${rawUrl}`;
			}

			let domain = new URL(validUrl).hostname;

			const ipRegex = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/;
			if (ipRegex.test(domain)) {
				return "IS_IP";
			}

			if (domain.startsWith("enterprise.")) {
				domain = domain.replace(/^enterprise\./i, "");
			}

			const systemRules = [
				{ regex: /zbx|zabbix/i, officialDomain: "zabbix.com" },
				{ regex: /voalle|\berp\b/i, officialDomain: "grupovoalle.com.br" },
				{ regex: /hubsoft/i, officialDomain: "hubsoft.com.br" },
				{ regex: /ixc/i, officialDomain: "ixcsoft.com.br" },
				{ regex: /sgp/i, officialDomain: "sgpsolucoes.com.br" },
			];

			for (const rule of systemRules) {
				if (rule.regex.test(validUrl)) {
					return `https://www.google.com/s2/favicons?domain=${rule.officialDomain}&sz=32`;
				}
			}

			return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
		} catch (error) {
			return "";
		}
	};

	return (
		<Grid sx={{ mb: 0.5 }}>
			<Card variant="outlined">
				<Box sx={styles.boxContent}>
					<CardActionArea
						data-active={selected ? "" : undefined}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							p: 2,
							border: selected ? `2px solid ${borderColor}` : "none",
							borderRight: "none",
							position: "relative",
							flexGrow: 1,
							"&::before": {
								content: '""',
								position: "absolute",
								left: 0,
								top: 0,
								width: "5px",
								height: "100%",
								bgcolor: borderColor,
								zIndex: 1,
							},
							"&[data-active]": {
								backgroundColor: "action.selected",
								"&:hover": {
									backgroundColor: "action.selectedHover",
								},
							},
						}}
						onClick={onClick}
					>
						{labelText && (
							<Box sx={{ bgcolor: borderColor, ...styles.labelTag }}>
								{labelText}
							</Box>
						)}

						{/* --- ÁREA DO TÍTULO E TAGS --- */}
						<Box
							sx={{
								p: 0.5,
								pt: labelText ? 1.5 : 0.5,
								flexGrow: 1,
								textAlign: "left",
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Typography
								sx={{ fontWeight: "bold", fontSize: "14px" }}
								component="span"
							>
								{`  ${title || "Sem título"}${ip ? ` - ${ip}` : ""}`}
							</Typography>

							<Box sx={styles.boxChips}>
								{displayTags.length > 0 ? (
									displayTags.map((tag) => (
										<Chip
											key={tag.id || Math.random()}
											label={tag.name}
											size="small"
											sx={{
												...styles.chipTags,
												bgcolor: alpha(tag?.color, 0.2),
												color: tag?.color,
												borderColor: tag?.color,
											}}
										/>
									))
								) : (
									<Typography
										sx={{
											fontSize: "12px",
											fontWeight: "bold",
											color: "text.secondary",
										}}
									>
										{/* Sem Tags */}
									</Typography>
								)}
							</Box>
						</Box>

						{/* --- ÁREA DOS ÍCONES WEB --- */}
						<Box sx={styles.boxCenter}>
							{isWebType &&
								webAccesses &&
								(() => {
									const validWebAccesses = webAccesses?.filter(
										(item) => item && item?.url,
									);

									const MAX_DISPLAY =
										windowWidth < 900
											? 4
											: windowWidth < 970
												? 1
												: windowWidth < 1100
													? 2
													: windowWidth < 1150
														? 3
														: 4;
									const visibleAccesses = validWebAccesses?.slice(
										0,
										MAX_DISPLAY,
									);
									const extraCount = validWebAccesses?.length - MAX_DISPLAY;

									return (
										<>
											{visibleAccesses.map((webItem, index) => (
												<Box key={index} sx={styles.boxCenter}>
													<Tooltip title={webItem.url} arrow>
														<IconButton
															size="small"
															onClick={(e) => {
																e.stopPropagation();
																handleOpenUrl(webItem.url);
															}}
														>
															{(() => {
																const faviconUrl = getFaviconUrl(webItem.url);

																if (faviconUrl === "IS_IP") {
																	return (
																		<Box
																			sx={{
																				...styles.icons,
																				display: "flex",
																				justifyContent: "center",
																				alignItems: "center",
																			}}
																		>
																			<LinkOutlined fontSize="small" />
																		</Box>
																	);
																}

																return (
																	<Box
																		component="img"
																		src={faviconUrl}
																		alt="favicon"
																		sx={{
																			...styles.icons,
																			p: 0.5,
																		}}
																		onError={(e) => {
																			e.target.style.display = "none";
																		}}
																	/>
																);
															})()}
														</IconButton>
													</Tooltip>
												</Box>
											))}

											{extraCount > 0 && (
												<Tooltip title={`Mais ${extraCount} acessos`} arrow>
													<Box
														sx={{
															...styles.icons,
															display: "flex",
															justifyContent: "center",
															alignItems: "center",
															fontSize: "14px",
															fontWeight: 900,

															ml: 0.5,
														}}
													>
														+{extraCount}
													</Box>
												</Tooltip>
											)}
										</>
									);
								})()}
						</Box>
					</CardActionArea>

					<CardMedia
						sx={{
							width: "auto",
							minWidth: "40px",
							display: "flex",

							border: selected ? `2px solid ${borderColor}` : "none",
							bgcolor: selected
								? lighten(theme.palette.background.paper, 0.1)
								: darken(theme.palette.background.paper, 0.1),
							borderLeft: "none",
							position: "relative",
						}}
					>
						<CardActionArea
							onClick={handleDeleteClick}
							sx={styles.actionButtonDelete}
						>
							<DeleteOutlineOutlined
								className="delete-icon"
								fontSize="small"
								color="error"
							/>
						</CardActionArea>
					</CardMedia>
				</Box>
			</Card>
		</Grid>
	);
}
